'use client'

import { useRef, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getCategories, getListingsByCategory, type MeiliListing, type MeiliCategory } from '@/app/lib/meilisearch'
import { getCategoryDisplayName } from '@/app/lib/constants'
import HeroSection from '@/components/HeroSection'
import CategoryPosters from '@/components/CategoryPosters'
import CategoryRow from '@/components/CategoryRow'
import Footer from '@/components/Footer'

export default function Home() {
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const { data: categories = [] } = useQuery<MeiliCategory[]>({
    queryKey: ['meilisearch-categories'],
    queryFn: getCategories,
    staleTime: 60 * 60 * 1000,
  })

  const handleCategoryClick = useCallback((categoryName: string) => {
    const el = categoryRefs.current[categoryName]
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  return (
    <div className="-mt-[72px]">
      <HeroSection />
      <CategoryPosters onCategoryClick={handleCategoryClick} />
      {categories.map((category) => (
        <div
          key={category.name}
          ref={(el) => { categoryRefs.current[category.name] = el }}
          className="scroll-mt-[72px]"
        >
          <CategoryRowWithData category={category} />
        </div>
      ))}
      <Footer />
    </div>
  )
}

function CategoryRowWithData({ category }: { category: MeiliCategory }) {
  const { data } = useQuery<{ hits: MeiliListing[] }>({
    queryKey: ['meilisearch-category', category.name],
    queryFn: () => getListingsByCategory(category.name, { limit: 20, offset: 0 }),
    staleTime: 60 * 60 * 1000,
  })

  return (
    <CategoryRow
      title={getCategoryDisplayName(category.name)}
      count={category.count}
      videos={data?.hits ?? []}
      categoryName={category.name}
    />
  )
}
